import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, Mail, Building2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { apiService } from '../../services/apiService';
import { getLogoUrl, handleLogoError } from '../../utils/logoUtils';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Password reset states
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const { login } = useAuth();
  const { settings } = useSettings();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Check if email/username is provided
    if (!formData.email) {
      newErrors.email = 'Email or username is required';
    } else if (formData.email.includes('@') && !/\S+@\S+\.\S+/.test(formData.email)) {
      // Only validate email format if it contains @ symbol
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Prepare login credentials - send email if it looks like an email, otherwise send as username
      const loginCredentials: { password: string; email?: string; username?: string } = {
        password: formData.password
      };

      // Check if the input looks like an email (contains @ symbol)
      if (formData.email && formData.email.includes('@')) {
        // It's an email
        loginCredentials.email = formData.email;
      } else if (formData.email) {
        // It's a username
        loginCredentials.username = formData.email;
      }

      await login(loginCredentials);
    } catch (error) {
      // Error is handled by the auth context
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetEmail || !/\S+@\S+\.\S+/.test(resetEmail)) {
      setResetMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setIsResetting(true);
    setResetMessage(null);
    
    try {
      // Call the password reset endpoint
      await apiService.post('/auth/reset-password', { email: resetEmail });
      setResetMessage({ 
        type: 'success', 
        text: 'Password reset successful! New password: admin123. Please change it after logging in.' 
      });
      setTimeout(() => {
        setShowResetModal(false);
        setResetEmail('');
        setResetMessage(null);
      }, 3000);
    } catch (error: any) {
      setResetMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to reset password. Please try again.' 
      });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Company Info */}
        <motion.div
          initial={{ opacity: 0, y: -20 } as any}
          animate={{ opacity: 1, y: 0 } as any}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <img 
              src="/The Aircons Ltd. Logo Org.png" 
              alt={settings?.company?.name || 'The Aircons Ltd.'}
              className="h-12 w-auto max-h-12"
              onLoad={() => {}}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <p className="text-[var(--color-text-secondary)] text-lg font-medium">
            Inventory Management System
          </p>
        </motion.div>

        {/* Login Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 } as any}
          animate={{ opacity: 1, y: 0 } as any}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-[var(--color-surface)] shadow-2xl p-8 rounded-lg font-[var(--font-family)] text-[var(--color-text)] border border-[var(--color-border)]"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2 text-[var(--color-text)] font-[var(--font-family)]">
              Welcome Back
            </h2>
            <p className="mb-2 text-[var(--color-text-secondary)] font-[var(--font-family)]">
              Sign in to your account to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email or Username Field */}
            <label htmlFor="email" className="block text-sm font-medium mb-2 text-[var(--color-text)] font-[var(--font-family)]">
              Email or Username
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-[var(--color-text-secondary)]" />
              </div>
              <input
                type="text"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`block w-full pl-10 pr-3 py-3 border focus:outline-none focus:ring-2 focus:border-transparent transition-colors rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-[var(--font-family)] ${errors.email ? 'border-[var(--color-error)] text-[var(--color-error)] placeholder-[var(--color-error)] focus:ring-[var(--color-error)]' : 'border-[var(--color-border)] placeholder-[var(--color-text-secondary)] focus:ring-[var(--color-primary)]'}`}
                placeholder="Enter your email or username"
                disabled={isLoading}
              />
            </div>
            {errors.email && (
              <motion.p
                initial={{ opacity: 0, y: -10 } as any}
                animate={{ opacity: 1, y: 0 } as any}
                className="mt-1 text-sm text-[var(--color-error)] font-[var(--font-family)]"
              >
                {errors.email}
              </motion.p>
            )}

            {/* Password Field */}
            <label htmlFor="password" className="block text-sm font-medium mb-2 text-[var(--color-text)] font-[var(--font-family)]">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-[var(--color-text-secondary)]" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`block w-full pl-10 pr-3 py-3 border focus:outline-none focus:ring-2 focus:border-transparent transition-colors rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-[var(--font-family)] ${errors.password ? 'border-[var(--color-error)] text-[var(--color-error)] placeholder-[var(--color-error)] focus:ring-[var(--color-error)]' : 'border-[var(--color-border)] placeholder-[var(--color-text-secondary)] focus:ring-[var(--color-primary)]'}`}
                placeholder="Enter your password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"
                disabled={isLoading}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && (
              <motion.p
                initial={{ opacity: 0, y: -10 } as any}
                animate={{ opacity: 1, y: 0 } as any}
                className="mt-1 text-sm text-[var(--color-error)] font-[var(--font-family)]"
              >
                {errors.password}
              </motion.p>
            )}
            
            {/* Forgot Password Link */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowResetModal(true)}
                className="text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] transition-colors font-[var(--font-family)]"
                disabled={isLoading}
              >
                Forgot Password?
              </button>
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              className="w-full flex items-center justify-center px-4 py-3 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg font-[var(--font-family)]"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <LoadingSpinner size="sm" color="surface" />
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-[var(--color-text-secondary)]">
              © {new Date().getFullYear()} {settings?.company?.name || 'The Aircons Ltd.'}. All rights reserved.
            </p>
          </div>
        </motion.div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 } as any}
          animate={{ opacity: 1 } as any}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-6"
        >
          <p className="text-[var(--color-text-secondary)] text-sm">
            Secure access to your inventory management system
          </p>
        </motion.div>
        
        {/* Password Reset Modal */}
        {showResetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 } as any}
              animate={{ opacity: 1, scale: 1 } as any}
              className="bg-[var(--color-surface)] rounded-lg p-6 w-full max-w-md shadow-2xl"
            >
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-[var(--color-text)] font-[var(--font-family)] mb-2">
                  Reset Password
                </h3>
                <p className="text-[var(--color-text-secondary)] font-[var(--font-family)]">
                  Enter your email to reset the admin password
                </p>
              </div>
              
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div>
                  <label htmlFor="resetEmail" className="block text-sm font-medium mb-2 text-[var(--color-text)] font-[var(--font-family)]">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="resetEmail"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="block w-full px-3 py-3 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-[var(--font-family)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                    placeholder="admin@theaircons.com"
                    disabled={isResetting}
                  />
                </div>
                
                {resetMessage && (
                  <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                    resetMessage.type === 'success' 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {resetMessage.type === 'success' ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <AlertCircle className="h-5 w-5" />
                    )}
                    <span className="text-sm font-[var(--font-family)]">{resetMessage.text}</span>
                  </div>
                )}
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetModal(false);
                      setResetEmail('');
                      setResetMessage(null);
                    }}
                    className="flex-1 px-4 py-3 border border-[var(--color-border)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors font-[var(--font-family)]"
                    disabled={isResetting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-[var(--font-family)] flex items-center justify-center"
                    disabled={isResetting}
                  >
                    {isResetting ? (
                      <div className="flex items-center space-x-2">
                        <LoadingSpinner size="sm" color="surface" />
                        <span>Resetting...</span>
                      </div>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage; 